import { Link } from "react-router-dom";
import { ArrowRight, Award, Users, BookOpen, Sparkles, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { motion } from "framer-motion";

const fadeSlideUp = (delay: number) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as [number, number, number, number], delay },
});

export function HeroSection() {
  const { t, getLocalizedRoute } = useLanguage();

  return (
    <section className="relative overflow-hidden min-h-[90vh] flex items-center" style={{ background: "linear-gradient(135deg, #164194 0%, #0d2a5e 100%)" }}>
      {/* Animated GodRays effect */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="hero-godray hero-godray-1" />
        <div className="hero-godray hero-godray-2" />
        <div className="hero-godray hero-godray-3" />

        {/* Radial glow center */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[500px] rounded-full opacity-[0.12] blur-[100px]"
          style={{ background: "radial-gradient(ellipse, hsl(219, 75%, 33%) 0%, transparent 60%)" }}
        />
        {/* Accent glow bottom */}
        <div
          className="absolute -bottom-40 left-1/4 w-[500px] h-[500px] rounded-full opacity-[0.10] blur-[120px]"
          style={{ background: "radial-gradient(circle, #B03A2E 0%, transparent 70%)" }}
        />
        {/* Subtle grid */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        />
      </div>

      <div className="container-dqs relative z-10">
        <div className="py-20 lg:py-28">
          <div className="mx-auto max-w-3xl text-center">
            {/* Badge */}
            <motion.div {...fadeSlideUp(0)} className="mb-8 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.05] px-4 py-1.5 text-sm text-white/70 backdrop-blur-sm">
              <Sparkles className="h-3.5 w-3.5 text-accent" />
              <span>{t.heroBadge}</span>
            </motion.div>

            {/* Headline */}
            <motion.h1 {...fadeSlideUp(0.1)} className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-[3.5rem] lg:leading-[1.1]">
              {t.heroTitle1}{" "}
              <span className="bg-gradient-to-r from-[#E63946] via-[#E63946] to-[#FF8A80] bg-clip-text text-transparent">
                {t.heroTitle2}
              </span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p {...fadeSlideUp(0.2)} className="mt-6 text-lg leading-relaxed text-white/60 sm:text-xl max-w-2xl mx-auto">
              {t.heroSubtitle}
            </motion.p>

            {/* CTAs */}
            <motion.div {...fadeSlideUp(0.3)} className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                size="lg"
                className="bg-accent hover:bg-accent/90 text-accent-foreground text-base px-8 h-12"
                asChild
              >
                <Link to={getLocalizedRoute('catalog')}>
                  {t.exploreCourses}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button
                size="lg"
                className="border border-primary-foreground/20 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 text-base px-8 h-12 backdrop-blur-sm"
                asChild
              >
                <Link to={getLocalizedRoute('companies')}>
                  {t.corporateSolutions}
                </Link>
              </Button>
            </motion.div>

            {/* Stats */}
            <motion.div {...fadeSlideUp(0.4)} className="mt-20 grid grid-cols-2 gap-8 border-t border-white/[0.08] pt-10 sm:grid-cols-4">
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <BookOpen className="h-5 w-5 text-accent/80" />
                </div>
                <div className="text-3xl font-bold text-white">500+</div>
                <div className="text-sm text-white/50">{t.coursesCompleted}</div>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <Users className="h-5 w-5 text-accent/80" />
                </div>
                <div className="text-3xl font-bold text-white">10.000+</div>
                <div className="text-sm text-white/50">{t.trainedProfessionals}</div>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <Building2 className="h-5 w-5 text-accent/80" />
                </div>
                <div className="text-3xl font-bold text-white">300+</div>
                <div className="text-sm text-white/50">{t.companiesServed}</div>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <Award className="h-5 w-5 text-accent/80" />
                </div>
                <div className="text-3xl font-bold text-white">35+</div>
                <div className="text-sm text-white/50">{t.yearsExperience}</div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* GodRays CSS */}
      <style>{`
        .hero-godray {
          position: absolute;
          top: -50%;
          width: 200px;
          height: 200%;
          opacity: 0.04;
          transform-origin: top center;
          filter: blur(60px);
        }
        .hero-godray-1 {
          left: 20%;
          background: linear-gradient(180deg, hsl(219, 75%, 33%) 0%, transparent 80%);
          animation: godray-sway 8s ease-in-out infinite;
        }
        .hero-godray-2 {
          left: 55%;
          background: linear-gradient(180deg, #164194 0%, transparent 80%);
          animation: godray-sway 10s ease-in-out infinite reverse;
          opacity: 0.03;
          width: 280px;
        }
        .hero-godray-3 {
          left: 75%;
          background: linear-gradient(180deg, hsl(219, 75%, 33%) 0%, transparent 70%);
          animation: godray-sway 12s ease-in-out infinite;
          opacity: 0.025;
          width: 160px;
        }
        @keyframes godray-sway {
          0%, 100% { transform: rotate(-8deg) scaleX(1); }
          50% { transform: rotate(8deg) scaleX(1.2); }
        }
      `}</style>
    </section>
  );
}
